<script setup>
import { computed, ref, watch } from 'vue'
import ViewerIcon from './ViewerIcon.vue'

const props = defineProps({
  status: { type: String, default: 'idle' },
  progress: { type: Number, default: 0 },
  url: { type: String, default: '' },
  mode: { type: String, default: '' },
  saving: Boolean,
  saveProgress: { type: Number, default: 0 },
  savingAction: { type: String, default: '' },
  generated: Boolean,
  stageText: { type: String, default: '' },
  error: { type: String, default: '' },
  taskId: { type: String, default: '' },
  notificationAvailable: Boolean,
  retryable: { type: Boolean, default: true },
  resumeOnly: Boolean,
  canPause: Boolean,
  softwareRotated: Boolean
})

defineEmits(['cancel', 'close', 'save', 'share', 'retry', 'notify'])

const generatedStageCopy = {
  checking: '正在查看进度',
  preparing: '正在准备画面',
  submitting: '正在提交画面',
  queued: '正在排队',
  running: '视频生成中',
  saving: '快完成了'
}

const statusTitle = computed(() => {
  if (props.generated || props.status === 'processing') return '马上就好'
  const percent = Math.round(Math.max(0, Math.min(100, props.progress)))
  return `录制中 · ${percent}%`
})

const statusDetail = computed(() => {
  if (props.generated) return generatedStageCopy[props.status] || props.stageText || '视频生成中'
  if (props.status === 'processing') return '正在完成录制'
  return props.mode === 'trajectory' ? '沿拍摄轨迹移动' : '绕模型转一圈'
})

const videoElement = ref(null)
const videoPlaying = ref(false)
const videoCurrentTime = ref(0)
const videoDuration = ref(0)

const resetVideoState = () => {
  videoPlaying.value = false
  videoCurrentTime.value = 0
  videoDuration.value = 0
}

const syncVideoDuration = event => {
  const duration = Number(event.currentTarget?.duration)
  videoDuration.value = Number.isFinite(duration) && duration > 0 ? duration : 0
}

const syncVideoTime = event => {
  const currentTime = Number(event.currentTarget?.currentTime)
  videoCurrentTime.value = Number.isFinite(currentTime) ? currentTime : 0
}

const toggleVideoPlayback = async () => {
  const video = videoElement.value
  if (!video) return
  if (!video.paused && !video.ended) {
    video.pause()
    return
  }
  if (video.ended && videoDuration.value > 0) video.currentTime = 0
  try {
    await video.play()
  } catch {
    videoPlaying.value = false
  }
}

const seekVideo = event => {
  const video = videoElement.value
  if (!video) return
  const nextTime = Number(event.currentTarget?.value)
  if (!Number.isFinite(nextTime)) return
  video.currentTime = Math.max(0, Math.min(videoDuration.value || nextTime, nextTime))
  videoCurrentTime.value = video.currentTime
}

const formatVideoTime = value => {
  const seconds = Number.isFinite(Number(value)) ? Math.max(0, Math.floor(Number(value))) : 0
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

watch(() => props.url, resetVideoState)
watch(() => props.status, status => {
  if (status !== 'ready') resetVideoState()
})
</script>

<template>
  <section v-if="['recording', 'processing', 'checking', 'preparing', 'submitting', 'queued', 'running', 'saving'].includes(status)" class="video-recording-layer" role="dialog" aria-modal="true" :aria-label="generated ? '视频生成进度' : '视频录制进度'">
    <div class="video-recording-status" :class="{ 'is-generated': generated }">
      <div class="video-recording-status__scene" aria-hidden="true">
        <div class="video-recording-status__visual">
          <span class="video-recording-status__icon"><ViewerIcon :name="generated ? 'play' : 'camera'" /></span>
          <span v-if="!generated" class="video-recording-status__mark" :class="{ 'is-processing': status === 'processing' }"></span>
        </div>
      </div>
      <div class="video-recording-status__copy">
        <strong>{{ statusTitle }}</strong>
        <Transition name="video-recording-stage" mode="out-in">
          <p :key="statusDetail">{{ statusDetail }}</p>
        </Transition>
      </div>
      <div class="video-recording-status__meter">
        <div class="video-recording-status__progress" :class="{ 'is-indeterminate': generated || status === 'processing' }" :role="!generated && status === 'recording' ? 'progressbar' : undefined" :aria-valuenow="!generated && status === 'recording' ? Math.round(progress) : undefined" :aria-valuemin="!generated && status === 'recording' ? 0 : undefined" :aria-valuemax="!generated && status === 'recording' ? 100 : undefined">
          <i :style="generated || status === 'processing' ? {} : { width: `${Math.max(0, Math.min(100, progress))}%` }"></i>
        </div>
      </div>
      <button v-if="status === 'recording'" type="button" @click="$emit('cancel')">取消</button>
      <div v-else-if="generated && canPause" class="video-recording-status__actions video-recording-status__actions--progress" :class="{ 'is-centered': !(notificationAvailable && taskId) }">
        <button v-if="notificationAvailable && taskId" type="button" @click="$emit('notify')">生成完成提醒</button>
        <button type="button" @click="$emit('close')">稍后</button>
      </div>
    </div>
  </section>

  <section v-if="status === 'error'" class="video-recording-layer" role="dialog" aria-modal="true" aria-label="视频生成失败">
    <div class="video-recording-status video-recording-status--error">
      <div class="video-recording-status__scene" aria-hidden="true">
        <div class="video-recording-status__visual"><span class="video-recording-status__icon"><ViewerIcon name="info" /></span></div>
      </div>
      <strong>{{ retryable ? (resumeOnly ? '暂时没查到' : '这次没做好') : '无法生成视频' }}</strong>
      <p>{{ error || '视频生成失败，请稍后重试' }}</p>
      <div class="video-recording-status__actions" :class="{ 'is-single': !retryable }">
        <button type="button" @click="$emit('close')">{{ retryable ? '稍后' : '知道了' }}</button>
        <button v-if="retryable" type="button" class="is-primary" @click="$emit('retry')">{{ resumeOnly ? '再试' : '重试' }}</button>
      </div>
    </div>
  </section>

  <section v-if="status === 'ready'" class="video-recording-preview-layer">
    <div class="video-recording-dialog" :class="{ 'is-software-rotated': softwareRotated }" role="dialog" aria-modal="true" aria-label="视频预览">
      <header>
        <div><small>VIDEO</small><h2>{{ generated ? '生成视频' : '视频已生成' }}</h2></div>
        <button type="button" aria-label="关闭视频预览" @click="$emit('close')">×</button>
      </header>
      <video
        v-if="url"
        ref="videoElement"
        :src="url"
        :controls="!softwareRotated"
        playsinline
        webkit-playsinline
        preload="metadata"
        @loadedmetadata="syncVideoDuration"
        @durationchange="syncVideoDuration"
        @timeupdate="syncVideoTime"
        @play="videoPlaying = true"
        @pause="videoPlaying = false"
        @ended="videoPlaying = false"
        @click="softwareRotated && toggleVideoPlayback()"
      ></video>
      <div v-if="url && softwareRotated" class="video-recording-dialog__controls">
        <button type="button" :aria-label="videoPlaying ? '暂停视频' : '播放视频'" @click="toggleVideoPlayback">
          <ViewerIcon :name="videoPlaying ? 'pause' : 'play'" />
        </button>
        <input
          type="range"
          min="0"
          :max="videoDuration || 0"
          step="0.01"
          :value="videoCurrentTime"
          :disabled="!videoDuration"
          aria-label="视频进度"
          @input="seekVideo"
        >
        <span>{{ formatVideoTime(videoCurrentTime) }} / {{ formatVideoTime(videoDuration) }}</span>
      </div>
      <footer>
        <button type="button" :disabled="saving" @click="$emit('close')">完成</button>
        <button type="button" :disabled="saving" @click="$emit('share')">
          <ViewerIcon name="share" />{{ saving && savingAction === 'share' ? generated ? '处理中' : `上传中 ${Math.round(saveProgress)}%` : '分享' }}
        </button>
        <button class="is-primary" type="button" :disabled="saving" @click="$emit('save')">
          <ViewerIcon name="download" />{{ saving && savingAction === 'save' ? generated ? '处理中' : `上传中 ${Math.round(saveProgress)}%` : '保存视频' }}
        </button>
      </footer>
    </div>
  </section>
</template>
